import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import UserFormFields from './UserFormFields';
import { createUser } from './api';
import { emptyUserForm, UserFormValue, UsersMeta } from './types';
import { primaryBtnClass } from '../styles';

interface IndividualCreateUserProps {
  meta: UsersMeta;
}

const IndividualCreateUser: React.FC<IndividualCreateUserProps> = ({ meta }) => {
  const [value, setValue] = useState<UserFormValue>(emptyUserForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async () => {
    if (!value.email.trim()) {
      setError('Email address is required.');
      return;
    }
    if (!value.organization_id) {
      setError('Organization is required.');
      return;
    }
    if (!value.user_type_id) {
      setError('User type is required.');
      return;
    }
    const selectedType = meta.user_types.find((t) => t.id === value.user_type_id);
    if (selectedType?.key === 'catalyx' && !value.catalyx_role_id) {
      setError('Catalyx role is required.');
      return;
    }
    if (selectedType?.key === 'client' && !value.client_role_id) {
      setError('Client role is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createUser(value);
      setSuccess(`Created ${created.email}. They can sign in with this email.`);
      setValue(emptyUserForm());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-[#626262] mb-5">
        Create a single account. Required fields are marked with an asterisk.
      </p>
      {error && (
        <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
          {success}
        </div>
      )}
      <UserFormFields meta={meta} value={value} onChange={setValue} mode="create" />
      <div className="mt-5">
        <button
          type="button"
          disabled={saving}
          onClick={() => void submit()}
          className={primaryBtnClass}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
          Create user
        </button>
      </div>
    </div>
  );
};

export default IndividualCreateUser;
